import { useMemo, useState } from "react";
import QRCode from "qrcode";
import { Alert, Input } from "@marekl/adminer/components";
import styles from "./ProvisioningCode.module.css";

interface ProvisioningCodeProps {
  /** The worker token, in the clear — this is what provisioning *is*. */
  token: string;
  /** What the phone should call itself on the Workers page. */
  name: string;
}

/** The payload the SMS worker app reads. Its half is `protocol/Provisioning.kt`. */
export function provisioningPayload(url: string, token: string, name: string) {
  return JSON.stringify({ v: 1, url, token, name });
}

/**
 * An address a phone on mobile data cannot resolve. The admin is very often
 * open on one of these — a port-forward, a dev server, an SSH tunnel — and the
 * resulting code provisions a worker that can never poll, which is only
 * discovered after the token has been shown for the last time.
 */
function unreachableFromAPhone(url: string): boolean {
  let host: string;
  try {
    host = new URL(url).hostname;
  } catch {
    return false; // Let the field's own validation say so instead.
  }
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host === "::1" ||
    host.endsWith(".local") ||
    host.endsWith(".localhost")
  );
}

/**
 * The code a phone scans to provision itself.
 *
 * Setting a worker up by hand means typing an address and a forty-character
 * random token on a touchscreen, and the token is shown exactly once — so a
 * mistyped character means revoking it and starting again. This removes both.
 *
 * Drawn as SVG rather than a canvas so it stays sharp when someone zooms the
 * page or points a camera at a scaled window.
 */
export function ProvisioningCode({ token, name }: ProvisioningCodeProps) {
  // Defaulted, not fixed: whoever is looking at this knows the address the
  // phone has to reach and the browser does not.
  const [url, setUrl] = useState(window.location.origin);

  const modules = useMemo(() => {
    // Medium correction: enough to survive a camera at an angle without
    // inflating the code so far that the modules get small on screen.
    const qr = QRCode.create(provisioningPayload(url, token, name), {
      errorCorrectionLevel: "M",
    });
    const size = qr.modules.size;
    const data = qr.modules.data;
    const cells: { x: number; y: number }[] = [];
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        if (data[y * size + x]) cells.push({ x, y });
      }
    }
    return { size, cells };
  }, [url, token, name]);

  // Four modules, which is what the standard requires. The surrounding card is
  // not white in the night theme, so the code has to carry its own.
  const quiet = 4;
  const span = modules.size + quiet * 2;

  return (
    <figure className={styles.wrap}>
      <svg
        className={styles.code}
        viewBox={`0 0 ${span} ${span}`}
        role="img"
        aria-label={`Provisioning code for ${name || "this worker"}`}
      >
        <rect width={span} height={span} className={styles.paper} />
        {modules.cells.map((cell) => (
          <rect
            key={`${cell.x}-${cell.y}`}
            x={cell.x + quiet}
            y={cell.y + quiet}
            width={1}
            height={1}
            className={styles.ink}
          />
        ))}
      </svg>
      <figcaption className={styles.caption}>
        Scan with the Letterman SMS worker app. A photograph of this code is as
        good as the token itself.
      </figcaption>
      <div className={styles.address}>
        <Input
          label="Address the phone will poll"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          hint="Must be reachable from the phone, not just from this browser."
        />
        {unreachableFromAPhone(url) ? (
          <Alert variant="warn">
            A phone cannot reach <code>{url}</code>. Put the address the worker
            will actually use here before scanning — the token is shown only
            once.
          </Alert>
        ) : null}
      </div>
    </figure>
  );
}
